import type { DownloadableFile } from '@/features/rag/types';

/**
 * EAS-365 (4b): delete a MIGRATED custom chatbot's documents from its CDS knowledge base, by document
 * id (`file.fileId`), via the per-tool DELETE route. The CDS twin of {@link removeFiles} (which deletes
 * by filename via the legacy OpenAI route). Returns the same `{ data | errorMsg }` shape the store expects.
 */
export const removeFilesFromCds = async (
  // Only name + fileId are read; narrowed so callers don't fabricate placeholder DownloadableFile fields.
  // Full DownloadableFile[] (e.g. onFilesRemoved) still satisfies this structurally.
  filesToRemove: Pick<DownloadableFile, 'name' | 'fileId'>[],
  customizationId: string,
): Promise<{
  errorMsg?: string;
  data?: { message: string; successfulDeletions: string[]; failedDeletions?: { file: string; error: string }[] };
}> => {
  const successfulDeletions: string[] = [];
  const failedDeletions: { file: string; error: string }[] = [];

  for (const file of filesToRemove) {
    const documentId = file.fileId;
    if (!documentId) {
      failedDeletions.push({ file: file.name, error: 'Missing document id' });
      continue;
    }
    try {
      const res = await fetch(`/api/tool-customizations/${customizationId}/documents/${documentId}`, { method: 'DELETE' });
      if (res.ok || res.status === 404) {
        // 404 = already gone; treat as success for the UI so the row clears.
        successfulDeletions.push(file.name);
      } else {
        failedDeletions.push({ file: file.name, error: res.statusText });
      }
    } catch (error) {
      failedDeletions.push({ file: file.name, error: error instanceof Error ? error.message : 'Delete failed' });
    }
  }

  return { data: { message: 'ok', successfulDeletions, failedDeletions } };
};
